import { onMounted, useStore } from '@nuxtjs/composition-api'
import { from, catchError, empty, tap, finalize } from 'rxjs'
import useComp from './useComp'
import useLocale from './useLocale'
import useErrorHandler from './useErrorHandler'

export default function usePushNotification(context) {
  let serviceWorkerRegistration = null
  let subscription = null

  onMounted(() => {
    // enable push notification
    if (!process.env.SUBSCRIBE_PUBLIC_KEY) return

    // check if it works
    if (!('serviceWorker' in navigator)) return
    if (!('Notification' in window)) {
      console.warn("Notifications aren't supported.")
      return
    }
    if (!('PushManager' in window)) {
      console.warn("Push messaging isn't supported.")
      return
    }

    from(navigator.serviceWorker.ready)
      .pipe(
        tap(registration => {
          serviceWorkerRegistration = registration

          // 取得訂閱資訊
          getSubscription().then(value => {
            subscription = value

            // 如果沒有訂閱資訊就詢問使用者是否要訂閱
            if (!subscription) {
              Notification.requestPermission().then()
            }
          })

          // 監聽訂閱通知事件
          navigator.permissions
            .query({ name: 'notifications' })
            .then(permissionStatus => {
              permissionStatus.onchange = function() {
                handleNotificationPermissionResult(this.state)
              }
            })
        }),
        catchError(err => {
          console.warn('navigator.serviceWorker.ready is warn: ', err)
          return empty()
        })
      )
      .subscribe()
  })

  const store = useStore()
  const { compId } = useComp()
  const { locale } = useLocale()
  const { errorHandler, errorHandler$ } = useErrorHandler(context)

  function getSubscription() {
    return serviceWorkerRegistration.pushManager
      .getSubscription()
      .catch(err => {
        console.warn('Error during getSubscription()', err)
      })
  }
  function handleNotificationPermissionResult(result) {
    console.log('User Choice:', result)
    switch (result) {
      case 'granted':
        if (!subscription) {
          subscribe$()
            .pipe(
              tap(() => {
                const title = context.root.$t('common.subscribeTitle')
                const options = {
                  body: context.root.$t('common.subscribeBody'),
                  icon: compId.value + '/icon.png',
                  dir: 'ltr',
                  lang: locale.value,
                  badge: compId.value + '/favicon.ico',
                  tag: 'confirm-notification',
                  renotify: true
                }
                serviceWorkerRegistration
                  .showNotification(title, options)
                  .then()
              })
            )
            .subscribe()
        }
        break
      case 'denied':
        console.warn('The user has blocked notifications.')
        subscription && unsubscribe(subscription)
        break
      case 'prompt':
        console.log('No notification permission granted!')
        subscription && unsubscribe(subscription)
        break
    }
  }
  function subscribe$() {
    return from(
      serviceWorkerRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertBase64StringToUint8Array(
          process.env.SUBSCRIBE_PUBLIC_KEY
        )
      })
    ).pipe(
      tap(value => {
        subscription = value
        sendSubscriptionToServer(subscription)
      }),
      catchError(err => {
        if (Notification.permission === 'denied') {
          console.warn('Permission for Notifications was denied')
        } else {
          console.error('Unable to subscribe to push.', err)
        }
        return empty()
      })
    )
  }
  function sendSubscriptionToServer(subscription) {
    const auth = subscription.getKey ? subscription.getKey('auth') : ''
    const p256dh = subscription.getKey ? subscription.getKey('p256dh') : ''

    store
      .dispatch('userSubscribe', {
        endpoint: subscription.endpoint,
        keys: {
          auth: auth
            ? btoa(String.fromCharCode.apply(null, new Uint8Array(auth)))
            : '',
          p256dh: p256dh
            ? btoa(String.fromCharCode.apply(null, new Uint8Array(p256dh)))
            : ''
        }
      })
      .catch(err => errorHandler(err))
  }
  function unsubscribe(subscription) {
    from(store.dispatch('userUnsubscribe', subscription))
      .pipe(
        catchError(err => errorHandler$(err)),
        finalize(() => (subscription = null))
      )
      .subscribe()
  }
}

function convertBase64StringToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding)
    // eslint-disable-next-line no-useless-escape
    .replace(/\-/g, '+')
    .replace(/_/g, '/')
  const rawData = window.atob(base64)
  const uint8Array = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    uint8Array[i] = rawData.charCodeAt(i)
  }
  return uint8Array
}
